import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoadingService } from '../../layout/services/loading/loading.service';
import { IPermission, IPermissionsAndRole, IUser } from '../../clients/models/user';
import { IResponse, IResponsePagination } from '@sharedModels/response-pagination.model';
import { IKeyValueGeneric } from '@sharedModels/key-value-generic.model';
import { CryptoJsService } from '@core/utils/crypto-js/crypto-js.service';
import { GlobalStateService } from 'src/app/shared/services/global-state/global-state.service';
import { ICompanyOwner } from '@modules/configs/pages/companies/services/companies.service';
import { toTitleCase } from 'src/app/shared/utils/utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // private httpClient = inject(HttpClient);
  private readonly hash = 'ye43@^(csd)m';

  constructor(
    private readonly loading: LoadingService,
    private readonly cryptoJsService: CryptoJsService,
    private readonly httpClient: HttpClient,
    private readonly globalState: GlobalStateService,
  ) {}

  getUser(): IUser {
    let user = localStorage.getItem('user');
    if (user) return this.cryptoJsService.decryptObject(user, this.hash);
    return {
      id: '0',
      email: 'noUser',
      companyOwner: { id: 0, nameCompany: 'No Company', isMain: false } as any,
    };
  }

  signIn(email: string, password: string): Promise<any> {
    const option: any = {
      email: email,
      password: password,
    };
    this.loading.loadingOn();
    return firstValueFrom(this.httpClient.post<any>(`${environment.apiUrl}/authentication/sign-in`, option))
      .then((res: any) => {
        const decipher = this.cryptoJsService.decryptObject(res.data, this.hash);
        if (res && decipher && decipher.access_token) {
          localStorage.setItem('access_token', `${decipher.access_token}`);
          if (decipher.user) {
            localStorage.setItem('user', this.cryptoJsService.encryptObject(decipher.user, this.hash));
            this.globalState.setSelectedCompanyId(decipher.user.companyOwner.id);
          }
          this.loading.loadingOff();
          return decipher;
        }
        this.loading.loadingOff();
        return null;
      })
      .catch((err) => {
        this.loading.loadingOff();
        return null;
      });
  }

  signUpAdmin(email: string, password: string, fullName: string, role: number, companyOwner: number): Promise<any> {
    const option: any = {
      email,
      password,
      fullName,
      role,
      companyOwner,
    };
    this.loading.loadingOn();
    return firstValueFrom(this.httpClient.post<any>(`${environment.apiUrl}/authentication/sign-up`, option))
      .then((res: any) => {
        if (res && res.data) {
          this.loading.loadingOff();
          return res.data;
        }
        this.loading.loadingOff();
        return null;
      })
      .catch((err) => {
        this.loading.loadingOff();
        return null;
      });
  }

  getAllUsers() {
    return firstValueFrom(this.httpClient.get<any>(`${environment.apiUrl}/user/all`))
      .then((res: any) => {
        if (res && res.data) {
          this.loading.loadingOff();
          return res.data;
        }
        this.loading.loadingOff();
        return null;
      })
      .catch((err) => {
        this.loading.loadingOff();
        return null;
      });
  }

  setNewPassword(id: string, password: string): Promise<any> {
    const option: any = {
      id: id,
      password: password,
    };
    this.loading.loadingOn();
    return firstValueFrom(this.httpClient.post<any>(`${environment.apiUrl}/authentication/reset`, option))
      .then((res: any) => {
        if (res && res.data) {
          this.loading.loadingOff();
          return res.data;
        }
        this.loading.loadingOff();
        return null;
      })
      .catch((err) => {
        this.loading.loadingOff();
        return null;
      });
  }

  resetPassword(email: string): Promise<any> {
    const option: any = {
      email: email,
    };
    this.loading.loadingOn();
    return firstValueFrom(this.httpClient.post<any>(`${environment.apiUrl}/authentication/recovery`, option))
      .then((res: any) => {
        if (res && res.data) {
          this.loading.loadingOff();
          return res.data;
        }
        this.loading.loadingOff();
        return null;
      })
      .catch((err) => {
        this.loading.loadingOff();
        return null;
      });
  }

  /**
   * getRols
   */
  public getRols() {
    return firstValueFrom<IResponsePagination<IKeyValueGeneric>>(
      this.httpClient.get<IResponsePagination<IKeyValueGeneric>>(`${environment.apiUrl}/role/all`),
    );
  }

  /**
   * getPermissions
   */
  public getPermissions(): IPermissionsAndRole {
    const user = this.getUser();
    const permissions = user.permissions ? user.permissions : [];
    const role = user.role ? user.role : { id: 0, name: 'No Role' };
    return { permission: permissions, role: role };
  }

  public hasPermissionCheck(permission: string): boolean {
    const permissions = this.getPermissions();
    return (
      permissions.role.name === 'admin' ||
      (permissions.permission || [])
        .map((module: IPermission) => module.slug)
        .some((slug: string) => slug === permission)
    );
  }

  /**
   * udpateRoleUser
   */
  public updateRoleUser(id: string, body: { role: number; companyOwner: number }) {
    return firstValueFrom(this.httpClient.patch(`${environment.apiUrl}/user/update-role/${id}`, body));
  }

  /**
   * getUserPermission
   */
  public getUserPermission(id: string) {
    return firstValueFrom<any>(this.httpClient.get(`${environment.apiUrl}/user/permission/${id}`));
  }

  /**
   * updateUserPermission
   */
  public updateUserPermission(id: string, body: { permissions: number[] }) {
    return firstValueFrom(this.httpClient.patch(`${environment.apiUrl}/user/update-permission/${id}`, body));
  }

  /**
   * logOut
   */
  public logOut() {
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
  }

  /**
   * getUserById
   */
  public getUserById(id: string) {
    return firstValueFrom(this.httpClient.get<IResponse<IUser>>(`${environment.apiUrl}/user/${id}`));
  }

  /**
   * getDashboardData
   */
  public getDashboardData(queryParams: { [key: string]: any }) {
    return firstValueFrom(
      this.httpClient.get(`${environment.apiUrl}/admin-dashboard/dashboard-data`, { params: queryParams }),
    );
  }

  setNewPasswordClient(id: string, password: string): Promise<any> {
    const option: any = {
      id: id,
      password: password,
    };
    this.loading.loadingOn();
    return firstValueFrom(
      this.httpClient.post<any>(`${environment.apiUrl}/authentication/reset-client-password`, option),
    )
      .then((res: any) => {
        if (res && res.data) {
          this.loading.loadingOff();
          return res.data;
        }
        this.loading.loadingOff();
        return null;
      })
      .catch((err) => {
        this.loading.loadingOff();
        return null;
      });
  }

  /**
   * blockUnLockUserAdmins
   */
  public blockUnLockUserAdmins(id: string) {
    return firstValueFrom(this.httpClient.get<any>(`${environment.apiUrl}/user/${id}/block-unlock-user-admin`));
  }

  /**
   * makeTabsItems
   */
  public makeTabsItems(
    companies: Array<ICompanyOwner>,
    routerLinkBase: string,
    permission: string,
  ): Promise<Array<any>> {
    return new Promise<Array<any>>((resolve) => {
      const items: Array<any> = [];
      const regex = /,\s*C\.A\.?/gi;
      if (this.getUser()?.companyOwner.isMain) {
        companies.forEach((company, index) => {
          items.push({
            label: `${toTitleCase(company.nameCompany.replace(regex, ''))}`,
            routerLink: `${routerLinkBase}-${index + 1}/${company.id}`,
            slug: permission,
          });
        });
      } else {
        items.push({
          label: `${toTitleCase(this.getUser().companyOwner.nameCompany.replace(regex, ''))}`,
          routerLink: `${routerLinkBase}-1/${this.getUser().companyOwner.id}`,
          slug: permission,
        });
      }
      if (items.length > 0) {
        resolve(items);
      }
    });
  }
}
